import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { supabase } from '@/lib/supabase-auth'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useProject } from '@/contexts/project-context'
import { usePermissions } from '@/lib/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { format } from 'date-fns'
import { toast } from 'sonner'
import {
  Target,
  Plus,
  Edit2,
  Trash2,
  TrendingUp,
  Users,
  Calendar,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Trophy,
  DollarSign,
  Package,
  UserCheck,
  Briefcase
} from 'lucide-react'
import MilestoneDetail from '@/components/custom-ui/milestone-detail'
import type { 
  Milestone, 
  MilestoneFormData, 
  MilestoneType,
  MilestoneStatus,
  MilestoneWithRelations 
} from '@/types/milestone'

const milestoneTypeIcons: Record<MilestoneType, React.ElementType> = {
  revenue: DollarSign,
  product: Package,
  user_growth: TrendingUp,
  fundraising: Briefcase,
  other: Target
}

const milestoneStatusIcons: Record<MilestoneStatus, React.ElementType> = {
  pending: Clock,
  in_progress: AlertCircle,
  achieved: CheckCircle2,
  expired: XCircle,
  cancelled: XCircle
}

const milestoneStatusColors: Record<MilestoneStatus, string> = {
  pending: 'text-gray-500 bg-gray-100',
  in_progress: 'text-blue-500 bg-blue-100',
  achieved: 'text-green-500 bg-green-100',
  expired: 'text-red-500 bg-red-100',
  cancelled: 'text-gray-500 bg-gray-100'
}

export default function MilestonesPage() {
  const getToken = useGetToken()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingMilestone, setEditingMilestone] = useState<MilestoneWithRelations | null>(null)
  const [selectedMilestone, setSelectedMilestone] = useState<MilestoneWithRelations | null>(null)
  const [activeTab, setActiveTab] = useState<MilestoneStatus | 'all'>('all')
  const queryClient = useQueryClient()
  const { currentProject } = useProject()
  const permissions = usePermissions()

  // Form state
  const [formData, setFormData] = useState<MilestoneFormData>({
    name: '',
    description: '',
    milestone_type: 'product',
    target_value: undefined,
    target_date: '',
    equity_class_id: undefined,
    equity_amount: undefined,
    equity_percentage: undefined,
    is_retroactive: false,
    requires_board_approval: false
  })

  // Fetch milestones
  const { data: milestones, isLoading } = useQuery({
    queryKey: ['milestones', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .from('milestones')
        .select(`
          *,
          equity_class:equity_class_id (
            id,
            class_name
          ),
          created_by_user:created_by (
            first_name,
            last_name
          ),
          achieved_by_user:achieved_by (
            first_name,
            last_name
          )
        `)
        .eq('project_id', currentProject.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data || []) as unknown as MilestoneWithRelations[]
    },
    enabled: !!currentProject
  })

  // Fetch equity classes for the dropdown
  const { data: equityClasses } = useQuery({
    queryKey: ['equity-classes', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .from('equity_classes')
        .select('*')
        .eq('project_id', currentProject.id)

      if (error) throw error
      return data || []
    },
    enabled: !!currentProject
  })

  // Create/Update milestone mutation
  const { mutate: saveMilestone, isPending: isSaving } = useMutation({
    mutationFn: async (data: MilestoneFormData & { id?: string }) => {
      if (!currentProject) throw new Error('No project selected')

      const payload = {
        ...data,
        project_id: currentProject.id,
        created_by: data.id ? undefined : (await supabase.auth.getUser()).data.user?.id
      }

      if (data.id) {
        const { error } = await supabase
          .from('milestones')
          .update(payload)
          .eq('id', data.id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('milestones')
          .insert(payload)

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestones'] })
      handleCloseDialog()
      toast.success(editingMilestone ? 'Milestone updated' : 'Milestone created')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  // Delete milestone mutation
  const { mutate: deleteMilestone } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('milestones')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestones'] })
      toast.success('Milestone deleted')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  const handleOpenDialog = (milestone?: MilestoneWithRelations) => {
    if (milestone) {
      setEditingMilestone(milestone)
      setFormData({
        name: milestone.name,
        description: milestone.description || '',
        milestone_type: milestone.milestone_type,
        target_value: milestone.target_value || undefined,
        target_date: milestone.target_date || '',
        equity_class_id: milestone.equity_class_id || undefined,
        equity_amount: milestone.equity_amount || undefined,
        equity_percentage: milestone.equity_percentage || undefined,
        is_retroactive: milestone.is_retroactive,
        requires_board_approval: milestone.requires_board_approval
      })
    } else {
      setEditingMilestone(null)
      setFormData({
        name: '',
        description: '',
        milestone_type: 'product',
        target_value: undefined,
        target_date: '',
        equity_class_id: undefined,
        equity_amount: undefined,
        equity_percentage: undefined,
        is_retroactive: false,
        requires_board_approval: false
      })
    }
    setShowAddDialog(true)
  }

  const handleCloseDialog = () => {
    setShowAddDialog(false)
    setEditingMilestone(null)
  }

  const handleSave = () => {
    saveMilestone({
      ...formData,
      id: editingMilestone?.id
    })
  }

  const filteredMilestones = milestones?.filter(m => 
    activeTab === 'all' || m.status === activeTab
  ) || []

  const getMilestoneStats = () => {
    if (!milestones) return { total: 0, pending: 0, in_progress: 0, achieved: 0 }

    return {
      total: milestones.length,
      pending: milestones.filter(m => m.status === 'pending').length,
      in_progress: milestones.filter(m => m.status === 'in_progress').length,
      achieved: milestones.filter(m => m.status === 'achieved').length
    }
  }

  const stats = getMilestoneStats()

  const formatValue = (value?: number, type?: MilestoneType) => {
    if (!value) return 'N/A'
    
    switch (type) {
      case 'revenue':
      case 'fundraising':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value)
      case 'user_growth':
        return value.toLocaleString() + ' users'
      default:
        return value.toLocaleString()
    }
  }

  // Show detail view if a milestone is selected
  if (selectedMilestone) {
    return (
      <MilestoneDetail
        milestone={selectedMilestone}
        projectId={currentProject?.id || ''}
        onBack={() => setSelectedMilestone(null)}
        onEdit={() => {
          handleOpenDialog(selectedMilestone)
          setSelectedMilestone(null)
        }}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Milestones</h1>
          <p className="mt-1 text-sm text-gray-600">
            Set performance targets that trigger automatic equity grants
          </p>
        </div>
        {permissions.canEditProject && (
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Milestone
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-gray-100">
              <Target className="h-6 w-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Milestones</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <AlertCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">In Progress</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.in_progress}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <Trophy className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Achieved</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.achieved}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Milestones Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="in_progress">In Progress</TabsTrigger>
          <TabsTrigger value="achieved">Achieved</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-gray-200 h-32 rounded-lg" />
              ))}
            </div>
          ) : filteredMilestones.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {activeTab === 'all' 
                  ? 'No milestones created yet' 
                  : `No ${activeTab.replace('_', ' ')} milestones`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMilestones.map((milestone) => {
                const TypeIcon = milestoneTypeIcons[milestone.milestone_type]
                const StatusIcon = milestoneStatusIcons[milestone.status]
                
                return (
                  <div key={milestone.id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 rounded-full bg-gray-100">
                            <TypeIcon className="h-5 w-5 text-gray-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">
                              {milestone.name}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {milestone.description}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Target</p>
                            <p className="text-sm font-medium">
                              {formatValue(milestone.target_value, milestone.milestone_type)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Target Date</p>
                            <p className="text-sm font-medium">
                              {milestone.target_date 
                                ? format(new Date(milestone.target_date), 'MMM d, yyyy')
                                : 'Not set'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Equity Reward</p>
                            <p className="text-sm font-medium">
                              {milestone.equity_amount 
                                ? `${milestone.equity_amount.toLocaleString()} shares`
                                : milestone.equity_percentage
                                ? `${milestone.equity_percentage}%`
                                : 'Not set'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Status</p>
                            <div className="flex items-center space-x-1">
                              <StatusIcon className="h-4 w-4" />
                              <Badge className={milestoneStatusColors[milestone.status]}>
                                {milestone.status.replace('_', ' ')}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        {milestone.status === 'achieved' && milestone.achieved_date && (
                          <div className="mt-3 text-sm text-green-600">
                            Achieved on {format(new Date(milestone.achieved_date), 'MMM d, yyyy')}
                            {milestone.achieved_by_user && (
                              <span className="text-gray-500">
                                {' '}by {milestone.achieved_by_user.first_name} {milestone.achieved_by_user.last_name}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {permissions.canEditProject && (
                        <div className="flex items-center space-x-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedMilestone(milestone)}
                          >
                            View Details
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(milestone)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this milestone?')) {
                                deleteMilestone(milestone.id)
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingMilestone ? 'Edit Milestone' : 'Create Milestone'}
            </DialogTitle>
            <DialogDescription>
              Define a performance target that can trigger automatic equity grants
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Milestone Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Series A Fundraise"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select 
                  value={formData.milestone_type} 
                  onValueChange={(value) => setFormData({ ...formData, milestone_type: value as MilestoneType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue Target</SelectItem>
                    <SelectItem value="product">Product Launch</SelectItem>
                    <SelectItem value="user_growth">User Growth</SelectItem>
                    <SelectItem value="fundraising">Fundraising</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the milestone and success criteria..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="target_value">Target Value</Label>
                <Input
                  id="target_value"
                  type="number"
                  value={formData.target_value || ''}
                  onChange={(e) => setFormData({ ...formData, target_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                  placeholder="e.g., 1000000 for $1M"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="target_date">Target Date</Label>
                <Input
                  id="target_date"
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Equity Grant (choose one)</Label>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="equity_class">Equity Class</Label>
                  <Select
                    value={formData.equity_class_id || ''}
                    onValueChange={(value) => setFormData({ ...formData, equity_class_id: value || undefined })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      {equityClasses?.map((ec: any) => (
                        <SelectItem key={ec.id} value={ec.id}>
                          {ec.class_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equity_amount">Fixed Shares</Label>
                  <Input
                    id="equity_amount"
                    type="number"
                    value={formData.equity_amount || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      equity_amount: e.target.value ? parseFloat(e.target.value) : undefined,
                      equity_percentage: undefined // Clear percentage if amount is set
                    })}
                    placeholder="e.g., 10000"
                    disabled={!!formData.equity_percentage}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equity_percentage">Or Percentage</Label>
                  <Input
                    id="equity_percentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.equity_percentage || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      equity_percentage: e.target.value ? parseFloat(e.target.value) : undefined,
                      equity_amount: undefined // Clear amount if percentage is set
                    })}
                    placeholder="e.g., 0.5"
                    disabled={!!formData.equity_amount}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="retroactive"
                  checked={formData.is_retroactive}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_retroactive: checked as boolean })}
                />
                <Label htmlFor="retroactive" className="text-sm font-normal cursor-pointer">
                  Can be applied retroactively
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="board_approval"
                  checked={formData.requires_board_approval}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_board_approval: checked as boolean })}
                />
                <Label htmlFor="board_approval" className="text-sm font-normal cursor-pointer">
                  Requires board approval to achieve
                </Label>
              </div>
            </div>

            {(formData.equity_amount || formData.equity_percentage) && !formData.equity_class_id && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please select an equity class for the grants
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={isSaving}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSaving || !formData.name}
            >
              {isSaving ? 'Saving...' : editingMilestone ? 'Update' : 'Create'} Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
